import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { Select, SelectOption } from '@/kit/Select';
import { SectionTabs } from '@/kit/SectionTabs';
import { Toggle } from '@/kit/Toggle';
import { openDialog } from '@/kit/dialog';
import {
  ReportNoteSection,
  type NoteEditorDialogData,
  type NoteEditorDialogResult,
  type PolicyTextDialogData,
  type ReportNoteTextInput,
  type ReportNoteTextRow,
} from '@/core/models/report-settings.models';
import type { DepreciationPolicyRow } from '@/core/models/phase7.models';
import { capiumTagGroupsFor } from '@/core/models/capium-tags';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { fixedAssetService } from '@/core/services/fixedAsset';
import { reportSettingsService } from '@/core/services/reportSettings';
import { CapitalisationDialog, FinancialDialog } from './PolicyTextDialog';
import { PrinciplesActivitesDialog } from './PrinciplesActivitesDialog';
import { SETTINGS_ROUTE, useReportClient } from './common';
import { NumberBox } from './NumberBox';
import { StickyReportSettingsFrame } from './StickyReportSettingsFrame';
import { useClientKind } from './clientKind';

interface PolicyItem {
  note: ReportNoteTextRow;
  name: string;
  checked: boolean;
  children?: PolicyItem[];
  /** 'view_list' for the two policies that open a fixed-wording dialog instead of the editor. */
  customIcon?: string;
  action?: 'financial' | 'capitalisation';
}

const financialInstrumentsKey = 'financialInstruments';
const capitalisationKey = 'capitalisationOfBorrowingCosts';

const depreciationMethods = [
  { value: 0, name: 'Straight line' },
  { value: 1, name: 'Reducing balance' },
];

const toItem = (note: ReportNoteTextRow): PolicyItem => ({ note, name: note.name, checked: note.isEnabled });

/** Report Settings > Accounting Policy: which policy paragraphs appear, their wording, and depreciation by asset class. */
export function AccountingPolicy() {
  const client = useReportClient();
  const clientType = useClientKind(client?.id)?.clientType;
  const [generalInformation, setGeneralInformation] = useState<ReportNoteTextRow | null>(null);
  const [leftItems, setLeftItems] = useState<PolicyItem[]>([]);
  const [leftsItems, setLeftsItems] = useState<PolicyItem[]>([]);
  /** ap5-a5: the depreciation / amortisation method and rate per asset class. */
  const [depreciation, setDepreciation] = useState<DepreciationPolicyRow[]>([]);
  const [isSavingDepreciation, setIsSavingDepreciation] = useState(false);
  /** The rates as last loaded or saved: the depreciation tab is "changed" (Unsaved changes, Discard, Save in the header) while they differ. */
  const [savedDepreciation, setSavedDepreciation] = useState<DepreciationPolicyRow[]>([]);
  const alive = useRef(true);
  const navigate = useNavigate();

  const applyDepreciation = (rows: DepreciationPolicyRow[]) => {
    setDepreciation(rows);
    setSavedDepreciation(rows);
  };
  const depreciationDirty = JSON.stringify(depreciation) !== JSON.stringify(savedDepreciation);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const apply = (rows: ReportNoteTextRow[]) => {
    setGeneralInformation(rows.find((n) => n.groupNo === 0) ?? null);
    setLeftItems(rows.filter((n) => n.groupNo === 1 && !n.parentKey).map(toItem));
    setLeftsItems(
      rows
        .filter((n) => n.groupNo === 2 && !n.parentKey)
        .map((note) => {
          const item = toItem(note);
          const children = rows.filter((c) => c.parentKey === note.noteKey).map(toItem);
          if (children.length > 0) item.children = children;
          if (note.noteKey === financialInstrumentsKey) {
            item.customIcon = 'view_list';
            item.action = 'financial';
          } else if (note.noteKey === capitalisationKey) {
            item.customIcon = 'view_list';
            item.action = 'capitalisation';
          }
          return item;
        }),
    );
  };

  useEffect(() => {
    if (!client) return;
    reportSettingsService.getReportNoteTexts(client.id, ReportNoteSection.AccountingPolicy).then(
      (rows) => alive.current && apply(rows),
      (error) => alive.current && notifyError(error),
    );
    fixedAssetService.getDepreciationPolicies(client.id).then(
      (view) => alive.current && applyDepreciation(view.classes ?? []),
      (error) => alive.current && notifyError(error),
    );
    // Both load once, in parallel, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNotes = (notes: ReportNoteTextInput[], successMessage?: string, revert?: () => void) => {
    if (!client) return;
    reportSettingsService.saveReportNoteTexts(client.id, ReportNoteSection.AccountingPolicy, notes).then(
      (rows) => {
        if (!alive.current) return;
        apply(rows);
        if (successMessage) notifySuccess(successMessage);
      },
      (error) => {
        if (!alive.current) return;
        revert?.();
        notifyError(error);
      },
    );
  };

  const setChecked = (noteKey: string, checked: boolean) => {
    const update = (items: PolicyItem[]): PolicyItem[] =>
      items.map((item) => ({
        ...item,
        checked: item.note.noteKey === noteKey ? checked : item.checked,
        children: item.children ? update(item.children) : undefined,
      }));
    setLeftItems(update);
    setLeftsItems(update);
  };

  const togglePolicy = (item: PolicyItem, checked: boolean) => {
    setChecked(item.note.noteKey, checked);
    saveNotes([{ noteKey: item.note.noteKey, isEnabled: checked, setText: false }], undefined, () =>
      setChecked(item.note.noteKey, !checked),
    );
  };

  const openEditor = (note: ReportNoteTextRow, isEnabled: boolean) => {
    const data: NoteEditorDialogData = {
      heading: note.name,
      title: note.title,
      content: note.content,
      defaultTitle: note.defaultTitle,
      defaultContent: note.defaultContent,
      tagGroups: capiumTagGroupsFor(clientType),
    };
    openDialog<NoteEditorDialogData, NoteEditorDialogResult>(PrinciplesActivitesDialog, { disableClose: true, data })
      .afterClosed()
      .then((result) => {
        if (result) {
          saveNotes(
            [{ noteKey: note.noteKey, isEnabled, setText: true, title: result.title, content: result.content }],
            'Accounting policy saved successfully.',
          );
        }
      });
  };

  const generalInfoDialog = () => {
    if (generalInformation) openEditor(generalInformation, true);
  };

  const editPolicy = (item: PolicyItem) => openEditor(item.note, item.checked);

  /** Saves the shown wording as the client's own, so later catalogue changes do not alter it. */
  const confirmWording = (item: PolicyItem) =>
    saveNotes(
      [
        {
          noteKey: item.note.noteKey,
          isEnabled: true,
          setText: true,
          title: item.note.title,
          content: item.note.content,
        },
      ],
      'Accounting policy saved successfully.',
    );

  const openWordingDialog = (item: PolicyItem, kind: 'financial' | 'capitalisation') => {
    const data: PolicyTextDialogData = { content: item.note.content };
    openDialog<PolicyTextDialogData, boolean>(kind === 'financial' ? FinancialDialog : CapitalisationDialog, {
      disableClose: true,
      data,
    })
      .afterClosed()
      .then((confirmed) => {
        if (confirmed) confirmWording(item);
      });
  };

  /** item 138: Saves the wording of all currently checked policies so they are persisted as answered in the statutory validation engine. */
  const confirmAllWording = () => {
    if (!client) return;
    const allChecked = [...leftItems, ...leftsItems].filter((i) => i.checked);
    if (allChecked.length === 0) {
      notifyError('No policies are currently selected.');
      return;
    }
    const notesToSave: ReportNoteTextInput[] = allChecked.map((i) => ({
      noteKey: i.note.noteKey,
      isEnabled: true,
      setText: true,
      title: i.note.title,
      content: i.note.content,
    }));
    saveNotes(notesToSave, 'All selected accounting policies confirmed and saved.');
  };

  /** ap5-a5: saves every class at once; a class left at its default is saved with that default. */
  const saveDepreciation = () => {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    if (depreciation.length === 0) return;
    setIsSavingDepreciation(true);
    fixedAssetService
      .saveDepreciationPolicies({
        clientId: client.id,
        policies: depreciation.map((row) => ({
          classCode: row.classCode,
          method: Number(row.method),
          ratePercent: Number(row.ratePercent) || 0,
        })),
      })
      .then(
        (view) => {
          if (!alive.current) return;
          applyDepreciation(view.classes ?? []);
          notifySuccess('Depreciation policies saved.');
        },
        (error) => alive.current && notifyError(error),
      )
      .finally(() => alive.current && setIsSavingDepreciation(false));
  };

  const changeRow = (index: number, patch: Partial<DepreciationPolicyRow>) =>
    setDepreciation((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <StickyReportSettingsFrame
      title="Accounting  Policy"
      onClose={() => navigate(SETTINGS_ROUTE)}
      // Design review 2 Nov 2026: the page's actions are top-right. Confirm Selected Policies is always there; the depreciation
      // rates are always editable and show Unsaved changes, Discard and Save once a rate differs from what was saved.
      dirty={depreciationDirty}
      busy={isSavingDepreciation}
      onSave={saveDepreciation}
      onDiscard={() => setDepreciation(savedDepreciation)}
      actions={
        <Button className="action-button primary-button" onClick={confirmAllWording}>
          Confirm Selected Policies
        </Button>
      }
    >
      <div className="mb-1">
        <p className="text-gray font-size-14 m-0">
          Selected policies must have their wording confirmed to satisfy statutory validation.
        </p>
      </div>
      {/* Design review 2 Nov 2026: the two policy lists and the depreciation rates are tabs, not cards side by side. */}
      <div className="mt-1">
        <SectionTabs
          ariaLabel="Accounting policy sections"
          tabs={[
            { id: 'pl', label: 'P&L Accounting Policies' },
            { id: 'bs', label: 'Balance Sheet Accounting Policies' },
            ...(depreciation.length > 0 ? [{ id: 'depreciation', label: 'Depreciation and Amortisation' }] : []),
          ]}
        >
          {(active) =>
            active === 'pl' ? (
              <div className="plc-wrapper w-387">
                <div className="plc-heading">
                  <p>P&L Accounting Policies</p>
                </div>
                <div className="p-1">
                  <div className="form-field d-flex align-items-center gap-5" style={{ marginBottom: '13px' }}>
                    <p className="m-0">General Information</p>
                    <Icon name="edit" outlined className="info-icon" onClick={generalInfoDialog} />
                  </div>
                  <div className="info-list-wrap">
                    <div className="info-list">
                      {leftItems.map((item) => (
                        <div key={item.note.noteKey} className="d-flex align-items-center gap-5">
                          <Toggle checked={item.checked} onCheckedChange={(checked) => togglePolicy(item, checked)}>
                            {item.name}
                          </Toggle>
                          {item.checked && (
                            <Icon name="edit" outlined className="info-icon" onClick={() => editPolicy(item)} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : active === 'bs' ? (
              <div className="plc-wrapper w-387">
                <div className="plc-heading">
                  <p>Balance Sheet Accounting Policies</p>
                </div>
                <div className="p-1">
                  <div className="info-list-wrap">
                    <div className="info-list">
                      {leftsItems.map((item) => (
                        <Fragment key={item.note.noteKey}>
                          <div className="d-flex align-items-center gap-5">
                            <Toggle checked={item.checked} onCheckedChange={(checked) => togglePolicy(item, checked)}>
                              {item.name}
                            </Toggle>
                            {item.checked && !item.customIcon && (
                              <Icon name="edit" outlined className="info-icon" onClick={() => editPolicy(item)} />
                            )}
                            {item.checked && item.customIcon && (
                              <Icon
                                name={item.customIcon}
                                outlined
                                className="info-icon"
                                onClick={() => openWordingDialog(item, item.action!)}
                              />
                            )}
                          </div>
                          {item.children && (
                            <div className="info-list pl-2">
                              {item.children.map((child) => (
                                <div key={child.note.noteKey} className="d-flex align-items-center gap-5">
                                  <Toggle
                                    checked={child.checked}
                                    onCheckedChange={(checked) => togglePolicy(child, checked)}
                                  >
                                    {child.name}
                                  </Toggle>
                                  {child.checked && (
                                    <Icon
                                      name="edit"
                                      outlined
                                      className="info-icon"
                                      onClick={() => editPolicy(child)}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="plc-wrapper mt-1">
                <div className="plc-heading">
                  <p>Depreciation and Amortisation - rate and method by asset class</p>
                </div>
                <div className="p-1">
                  <div className="mat-table-wrapper">
                    <table className="wp-100" aria-describedby="Depreciation policy by asset class">
                      <tbody>
                        <tr>
                          <th scope="col" className="text-left">
                            Asset class
                          </th>
                          <th scope="col" className="text-left">
                            Method
                          </th>
                          <th scope="col" className="text-left">
                            Rate %
                          </th>
                        </tr>
                        {depreciation.map((row, i) => (
                          <tr key={row.classCode}>
                            <td>{row.className}</td>
                            <td>
                              <Select
                                className="w-190"
                                name={`method${i}`}
                                clearable={false}
                                value={row.method}
                                onChange={(value) => changeRow(i, { method: value as number })}
                              >
                                {depreciationMethods.map((m) => (
                                  <SelectOption key={m.value} value={m.value}>
                                    {m.name}
                                  </SelectOption>
                                ))}
                              </Select>
                            </td>
                            <td>
                              <NumberBox
                                fieldClassName="w-100"
                                max={100}
                                maxIntDigits={3}
                                maxDecimals={2}
                                name={`rate${i}`}
                                value={row.ratePercent}
                                onValue={(value) => changeRow(i, { ratePercent: value as number })}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          }
        </SectionTabs>
      </div>
    </StickyReportSettingsFrame>
  );
}
